import { Link } from "react-router-dom";
import { fetchDashboardDataFromSheet } from "../../api/sheetsApi";
import { useEffect, useMemo, useState } from "react";
import {
  Bus,
  Building2,
  CalendarDays,
  ClipboardList,
  FileSpreadsheet,
  GraduationCap,
  Percent,
  SquarePen,
  UserCheck,
  UserRound,
  Users,
  Wallet,
} from "lucide-react";

const statStyles = [
  { icon: Users, bg: "bg-pink-50", iconBg: "bg-fuchsia-600", text: "text-fuchsia-600" },
  { icon: Building2, bg: "bg-sky-50", iconBg: "bg-blue-600", text: "text-sky-500" },
  { icon: ClipboardList, bg: "bg-indigo-50", iconBg: "bg-indigo-600", text: "text-indigo-600" },
  { icon: ClipboardList, bg: "bg-purple-50", iconBg: "bg-purple-600", text: "text-purple-600" },
  { icon: GraduationCap, bg: "bg-rose-50", iconBg: "bg-red-500", text: "text-red-500" },
  { icon: UserRound, bg: "bg-orange-50", iconBg: "bg-orange-500", text: "text-orange-600" },
  { icon: Users, bg: "bg-yellow-50", iconBg: "bg-yellow-500", text: "text-yellow-600" },
  { icon: Bus, bg: "bg-cyan-50", iconBg: "bg-cyan-500", text: "text-cyan-600" },
  { icon: Bus, bg: "bg-teal-50", iconBg: "bg-teal-500", text: "text-teal-600" },
  { icon: UserCheck, bg: "bg-amber-50", iconBg: "bg-amber-500", text: "text-amber-600" },
  { icon: Percent, bg: "bg-red-50", iconBg: "bg-red-500", text: "text-red-500" },
  { icon: Wallet, bg: "bg-lime-50", iconBg: "bg-lime-500", text: "text-lime-600" },
  { icon: FileSpreadsheet, bg: "bg-emerald-50", iconBg: "bg-emerald-500", text: "text-emerald-600" },
];

const hiddenEvents = new Set(["Academic Performance"]);

const parseNumber = (value) => {
  const parsed = Number(String(value || "").replace(/[^0-9.]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const clampPercent = (value) => `${Math.min(Math.max(value, 0), 100).toFixed(1)}%`;

const toPercentWidth = (value) => {
  if (typeof value === "string" && value.trim().endsWith("%")) {
    return value;
  }

  return clampPercent(parseNumber(value));
};

export default function Thiruvanmiyur() {
  const [campusData, setCampusData] = useState([]);
  const [currentMonth, setCurrentMonth] = useState("May 2026");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchThiruvanmiyurCampusData = async () => {
      try {
        const data = await fetchDashboardDataFromSheet();
        setCampusData(data?.thiruvanmiyurModelData || []);
        setCurrentMonth(data?.currentMonth || "May 2026");
      } catch (err) {
        console.error(err);
        setError(err.message || "Unable to load Thiruvanmiyur campus data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchThiruvanmiyurCampusData();
  }, []);

  const campusMap = useMemo(
    () =>
      campusData.reduce((acc, item) => {
        acc[item.events] = item.summary || "0";
        return acc;
      }, {}),
    [campusData]
  );

  const stats = useMemo(
    () =>
      campusData
        .filter((item) => !hiddenEvents.has(item.events))
        .map((item, index) => ({
          title: item.events,
          value: item.summary || "0",
          subtitle: "Thiruvanmiyur Campus",
          ...statStyles[index % statStyles.length],
        })),
    [campusData]
  );

  const staff = useMemo(
    () => [
      { label: `Teachers ${campusMap["Total Teachers"] || 0}`, color: "bg-blue-600" },
      { label: `Admin ${campusMap["Total Admin Staff"] || 0}`, color: "bg-red-500" },
      {
        label: `Housekeeping ${campusMap["Total Housekeeping Staff"] || 0}`,
        color: "bg-yellow-500",
      },
      { label: `Drivers ${campusMap["Total Drivers"] || 0}`, color: "bg-lime-500" },
    ],
    [campusMap]
  );

  const metrics = useMemo(() => {
    const students = parseNumber(campusMap["Total Students"]);
    const capacity = parseNumber(campusMap["Total Capacity"]);
    const classrooms = parseNumber(campusMap["Total Classrooms"]);
    const vacantClassrooms = parseNumber(campusMap["Total Vacant Classrooms"]);
    const busRoutes = parseNumber(campusMap["Total Bus Routes"]);
    const capacityUtilised = capacity ? clampPercent((students / capacity) * 100) : "0.0%";
    const vacantClassroomPercent = classrooms
      ? clampPercent((vacantClassrooms / classrooms) * 100)
      : "0.0%";
    const busCoverage = classrooms ? clampPercent((busRoutes / classrooms) * 100) : "0.0%";

    return [
      {
        label: "Capacity Utilised",
        value: capacityUtilised,
        width: capacityUtilised,
        color: "bg-fuchsia-600",
      },
      {
        label: "Fee Collected",
        value: campusMap["Term I Fees Collected %"] || "0%",
        width: toPercentWidth(campusMap["Term I Fees Collected %"]),
        color: "bg-blue-600",
      },
      {
        label: "TC Percentage",
        value: campusMap["Total TC Percentage 25-26"] || campusMap["Total TC Percentage"] || "0%",
        width: toPercentWidth(campusMap["Total TC Percentage 25-26"] || campusMap["Total TC Percentage"]),
        color: "bg-red-500",
      },
      {
        label: "Bus Coverage",
        value: busCoverage,
        width: busCoverage,
        color: "bg-yellow-500",
      },
      {
        label: "Vacant Classrooms",
        value: vacantClassroomPercent,
        width: vacantClassroomPercent,
        color: "bg-lime-500",
      },
    ];
  }, [campusMap]);

  if (loading) {
    return (
      <div className="p-2 bg-gray-100 min-h-[100dvh] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-gray-100 p-4">
        <div className="rounded-xl bg-white px-5 py-4 text-sm font-medium text-red-500 shadow-sm">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 bg-gray-100 min-h-[100dvh] space-y-6">
      <div className="flex">
        <div className="h-28 w-36 overflow-hidden shadow-lg">
          <img
            src="/THV.jpg"
            alt="school"
            className="h-full w-full object-cover"
          />
        </div>

        <div className="relative flex-1 overflow-hidden rounded-r-2xl bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 p-6 text-white shadow-lg">
          <div className="absolute right-10 top-0 h-40 w-40 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute bottom-0 left-1/3 h-24 w-24 rounded-full bg-pink-400/20 blur-2xl" />

          <div className="relative z-10 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-bold">Thiruvanmiyur Campus</h1>
              <p className="mt-1 text-sm text-purple-100">
                Established Campus
                <span className="mx-2">|</span>
                Chennai
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {/* Calendar Button */}
<button className="flex items-center justify-center gap-2 w-38 rounded-lg border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium backdrop-blur transition hover:bg-white/20">
  <CalendarDays size={18} />
  <span className="text-base">{currentMonth}</span>
</button>

{/* Edit Button */}
<Link data-html2canvas-ignore="true" to={"/edit/thiruvanmiyur"} className="flex items-center justify-center gap-2 w-38 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-purple-700 transition hover:bg-purple-50">
  <SquarePen size={18} />
  <span className="text-base">Edit</span>
</Link>

            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        {stats.map((item) => {
          const Icon = item.icon;

          return (
            <div
              key={item.title}
              className={`rounded-2xl p-4 shadow-sm ${item.bg}`}
            >
              <div
                className={`mb-2 flex h-8 w-8 items-center justify-center rounded-lg text-white ${item.iconBg}`}
              >
                <Icon size={16} />
              </div>

              <p className="text-sm text-gray-700">
                {item.title}
              </p>

              <h3 className="mt-1 break-words text-xl font-bold text-slate-800">
                {item.value}
              </h3>

              <p className={`mt-0.5 text-xs font-medium ${item.text}`}>
                {item.subtitle}
              </p>
            </div>
          );
        })}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="rounded-3xl bg-white p-6 shadow-sm">
          <h2 className="text-2xl font-semibold text-slate-800">
            Staff Breakdown
          </h2>

          <div className="mt-8 flex flex-col items-center gap-8 lg:flex-row">
            <div className="relative h-44 w-44">
              {/*<div
                className="h-full w-full rounded-full"
                style={{
                  background: `
                  conic-gradient(
                    #0ea5e9 0% 40%,
                    #ef4444 40% 50%,
                    #eab308 50% 75%,
                    #84cc16 75% 100%
                  )
                `,
                }}
              />*/}

              <div className="relative h-44 w-44">
              <div className="absolute inset-0 rounded-full border-[14px] border-sky-500 border-r-red-500 border-b-yellow-400 border-l-lime-500" />

              <div className="absolute inset-6 flex flex-col items-center justify-center rounded-full bg-white">
                <h3 className="text-3xl font-bold text-slate-800">100%</h3>
                <p className="text-xs text-gray-500">TOTAL</p>
              </div>
            </div>
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {staff.map((item) => (
                <div
                  key={item.label}
                  className="flex items-center gap-3"
                >
                  <div
                    className={`h-4 w-4 rounded-full ${item.color}`}
                  />

                  <span className="text-sm font-medium text-gray-700">
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-3xl bg-white p-6 shadow-sm">
          <h2 className="text-2xl font-semibold text-slate-800">
            Campus Metrics
          </h2>

          <div className="mt-8 space-y-8">
            {metrics.map((item) => (
              <div key={item.label}>
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600">
                    {item.label}
                  </span>

                  <span className="text-sm font-semibold text-slate-800">
                    {item.value}
                  </span>
                </div>

                <div className="h-2 overflow-hidden rounded-full bg-gray-200">
                  <div
                    className={`h-full rounded-full ${item.color}`}
                    style={{ width: item.width }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
